import { Button } from "@openanalyst-ai/ui/button"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { DropdownMenu } from "@openanalyst-ai/ui/dropdown-menu"
import { Icon } from "@openanalyst-ai/ui/icon"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { SessionTurn } from "@openanalyst-ai/ui/session-turn"
import { DataProvider } from "@openanalyst-ai/ui/context"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { showToast } from "@openanalyst-ai/ui/toast"
import { A } from "@solidjs/router"
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  type Accessor,
  type JSX,
  Show,
} from "solid-js"
import { createStore } from "solid-js/store"
import type { Message, Part, Session } from "@openanalyst-ai/sdk/v2/client"
import { attachmentData, attachmentMime } from "@/components/prompt-input/files"
import {
  FULL_ACCESS_CONFIRMATION_COPY,
  isPermissionMode,
  PERMISSION_MODE_OPTIONS,
  permissionModeOption,
  shouldConfirmFullAccessPermission,
} from "@/components/prompt-input/control-labels"
import { useGlobalSync } from "@/context/global-sync"
import { useLocal } from "@/context/local"
import { usePermission } from "@/context/permission"
import type { PermissionMode } from "@/context/permission-auto-respond"
import { usePlatform } from "@/context/platform"
import { useSDK } from "@/context/sdk"
import { useSync } from "@/context/sync"
import {
  appChatAcceptedFileTypes,
  appChatAttachmentUnsupported,
  appChatMimeSupported,
  type AppChatComposerAttachment,
} from "@/plugins/app-chat-attachments"
import { useCommand } from "@/context/command"
import { PluginAppIcon } from "@/plugins/plugin-app-icon"
import type { AppChatPageState } from "@/plugins/app-chat-context"
import {
  appSidebarArchiveFailedMessage,
  appSidebarPermissionFailedMessage,
  appSidebarRenameFailedMessage,
  appSidebarSendFailedMessage,
} from "@/pages/layout/app-sidebar-panel-copy"
import {
  appChatOptimisticParts,
  appChatRequestParts,
  resolvePluginParentSession,
  sendAppChatPrompt,
} from "@/plugins/app-chat"
import { pluginColorForIndex, resolveUniquePluginIcons } from "./sidebar-plugins"
import type { RuntimePluginApp } from "@/plugins/runtime"
import { Identifier } from "@/utils/id"
import { displayFilename } from "@/utils/display-path"
import { SessionQuestionDock } from "@/pages/session/composer/session-question-dock"
import { SessionPermissionDock } from "@/pages/session/composer/session-permission-dock"
import { sessionPermissionRequest } from "@/pages/session/composer/session-request-tree"
import {
  appSidebarAcceptedFileTypes,
  appSidebarPluginFullSessionTitle,
  appSidebarPluginAttachments,
  appSidebarPluginThreads,
  appSidebarQuestionRequest,
  appSidebarScrollbarState,
  appSidebarShouldReadNativeClipboard,
  appSidebarShouldSubmitFromKey,
  appSidebarTransferFiles,
  appSidebarVideoBoundary,
} from "./app-sidebar-panel-state"

const fallbackDescriptor = (pluginID: string): RuntimePluginApp => ({
  id: pluginID,
  title: pluginID || "Plugin App",
  icon: "sparkles",
  route: pluginID,
  viewToolName: "",
  capabilities: [],
})

type AppChatState = {
  prompt: string
  historyQuery: string
  renamingSessionID?: string
  renameValue: string
  sending: boolean
  sent: boolean
  dragging: boolean
  focusCue: boolean
  scrolling: boolean
  scrollbar: {
    visible: boolean
    top: number
    height: number
  }
  attachments: AppChatComposerAttachment[]
}

const threadTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
})

export function AppSidebarPanel(props: {
  directory: string
  slug: string
  pluginID: string
  page: Accessor<AppChatPageState | undefined>
  sessionID: Accessor<string | undefined>
  parentSessionID?: Accessor<string | undefined>
  onSessionID: (sessionID: string) => void
  onNewSession: () => void
  onRenameSession?: (session: Session, title: string) => void | Promise<void>
  onArchiveSession?: (session: Session) => void | Promise<void>
}) {
  const sdk = useSDK()
  const sync = useSync()
  const local = useLocal()
  const platform = usePlatform()
  const globalSync = useGlobalSync()
  const permission = usePermission()
  const dialog = useDialog()
  const command = useCommand()
  let fileInput: HTMLInputElement | undefined
  let promptInput: HTMLTextAreaElement | undefined
  let panelRoot: HTMLDivElement | undefined
  let scrollArea: HTMLDivElement | undefined
  let scrollbarDrag:
    | {
        pointerID: number
        startY: number
        startScrollTop: number
        maxScroll: number
        maxThumbTravel: number
      }
    | undefined
  const [state, setState] = createStore<AppChatState>({
    prompt: "",
    historyQuery: "",
    renameValue: "",
    sending: false,
    sent: false,
    dragging: false,
    focusCue: false,
    scrolling: false,
    scrollbar: { visible: false, top: 0, height: 100 },
    attachments: [],
  })
  const [panelFocused, setPanelFocused] = createSignal(false)
  let scrollFadeTimer: ReturnType<typeof setTimeout> | undefined
  let focusCueTimer: ReturnType<typeof setTimeout> | undefined
  let promptResizeFrame: number | undefined
  // Auto-scroll ("stick to bottom"): the transcript follows the AI as it streams,
  // but ONLY while the user is parked at the bottom. The moment they scroll up to
  // read earlier output we release the pin and stop yanking them down; when they
  // scroll back to the bottom it re-pins. `transcriptEl` is observed for size
  // growth (streaming tokens, tool chips) so we re-pin on every content change.
  let transcriptEl: HTMLDivElement | undefined
  let transcriptResizeObserver: ResizeObserver | undefined
  let stickyScrollFrame: number | undefined
  let stickToBottom = true
  const STICK_TO_BOTTOM_THRESHOLD = 64

  const descriptor = createMemo(() => props.page()?.descriptor ?? fallbackDescriptor(props.pluginID))
  const title = createMemo(() => descriptor().title || props.pluginID || "Plugin App")

  // Sync the header icon with what the left rail shows for this plugin —
  // same unique-resolution (brand → intent → fallback) and same accent color.
  const [pluginApps] = createResource(
    () => props.directory,
    async (directory) => {
      if (!directory) return [] as RuntimePluginApp[]
      const result = await sdk.client.plugin.apps()
      return (result.data ?? []) as RuntimePluginApp[]
    },
  )
  const orderedPlugins = createMemo(() => {
    const seen = new Set<string>()
    return (pluginApps() ?? [])
      .toReversed()
      .filter((app) => (seen.has(app.id) ? false : (seen.add(app.id), true)))
      .toReversed()
  })
  const railResolved = createMemo(() => resolveUniquePluginIcons(orderedPlugins()))
  const activeIndex = createMemo(() => orderedPlugins().findIndex((app) => app.id === props.pluginID))
  const activeResolved = createMemo(() => {
    const idx = activeIndex()
    if (idx < 0) return undefined
    return railResolved()[idx]
  })
  const activeAccent = createMemo(() => {
    const r = activeResolved()
    if (r?.kind === "brand") return r.brand.hex
    const idx = activeIndex()
    if (idx < 0) return undefined
    return pluginColorForIndex(idx)
  })
  // For the PluginAppIcon glyph: brand items keep the descriptor's icon
  // string (so brand glyphs render via the brand-aware Icon set);
  // intent items pass the resolved IconName as the icon prop.
  const railSyncedIcon = createMemo(() => {
    const r = activeResolved()
    if (r?.kind === "icon") return r.name
    return descriptor().icon
  })
  const railSyncedBrandPath = createMemo(() => {
    const r = activeResolved()
    return r?.kind === "brand" ? r.brand.path : undefined
  })
  const accentStyle = createMemo<JSX.CSSProperties | undefined>(() => {
    const accent = activeAccent()
    if (!accent) return undefined
    return { color: accent }
  })

  // Permission mode: per-session if we have one, else per-directory. The
  // global permission context auto-responds to AI permission asks based on
  // this mode, so whatever the user picks here is honored during the chat.
  const currentPermissionMode = createMemo<PermissionMode>(() => {
    const id = props.sessionID()
    if (!id) return permission.directoryPermissionMode(props.directory)
    return permission.permissionMode(id, props.directory)
  })
  const currentPermissionOption = createMemo(() => permissionModeOption(currentPermissionMode()))
  const [permissionMenuOpen, setPermissionMenuOpen] = createSignal(false)
  const applyPermissionMode = (mode: PermissionMode) => {
    const id = props.sessionID()
    if (id) {
      permission.setPermissionMode(mode, id, props.directory)
      return
    }
    permission.setDirectoryPermissionMode(mode, props.directory)
  }
  const showFullAccessConfirmation = () => {
    dialog.show(() => (
      <Dialog
        fit
        title={FULL_ACCESS_CONFIRMATION_COPY.title}
        action={<span aria-hidden="true" class="size-8" />}
        class="max-w-[520px] rounded-[18px] bg-surface-raised-stronger-non-alpha"
      >
        <div class="flex w-full flex-col gap-5 px-5 pb-5">
          <div class="space-y-3 text-14-regular leading-6 text-text-base">
            <p>{FULL_ACCESS_CONFIRMATION_COPY.description}</p>
            <p>{FULL_ACCESS_CONFIRMATION_COPY.warning}</p>
          </div>
          <div class="flex justify-end gap-3">
            <Button type="button" variant="secondary" autofocus class="rounded-[10px]" onClick={() => dialog.close()}>
              {FULL_ACCESS_CONFIRMATION_COPY.cancel}
            </Button>
            <Button
              type="button"
              variant="secondary"
              class="rounded-[10px] !border-transparent !bg-surface-critical-base !text-icon-critical-base !shadow-none hover:!bg-surface-critical-weak"
              onClick={() => {
                applyPermissionMode("full-access")
                dialog.close()
              }}
            >
              {FULL_ACCESS_CONFIRMATION_COPY.confirm}
            </Button>
          </div>
        </div>
      </Dialog>
    ))
  }
  const selectPermissionMode = (mode: PermissionMode) => {
    setPermissionMenuOpen(false)
    if (shouldConfirmFullAccessPermission(currentPermissionMode(), mode)) {
      showFullAccessConfirmation()
      return
    }
    applyPermissionMode(mode)
  }

  const acceptedFileTypes = createMemo(() => appSidebarAcceptedFileTypes(appChatAcceptedFileTypes(local.model.current())))
  const attachmentsEnabled = createMemo(() => acceptedFileTypes().length > 0)
  const canSubmit = createMemo(() => Boolean(state.prompt.trim() || state.attachments.length) && !state.sending)
  const sessionHref = createMemo(() => {
    const id = props.sessionID()
    if (!id) return ""
    return `/${props.slug}/session/${id}`
  })
  const messages = createMemo(() => {
    const id = props.sessionID()
    if (!id) return [] as Message[]
    return sync.data.message[id] ?? []
  })
  const questionRequest = createMemo(() =>
    appSidebarQuestionRequest({
      session: sync.data.session,
      question: sync.data.question,
      sessionID: props.sessionID(),
    }),
  )
  // Tool PERMISSION asks for THIS app-chat session. The panel previously only
  // docked AskUserQuestion (questions), so a tool that needed permission had its
  // ask rendered nowhere → the turn got stuck. Mirror the main composer: surface
  // the permission request (skipping ones the permission mode auto-responds to)
  // and answer it via the same permission.respond endpoint.
  const permissionRequest = createMemo(() =>
    sessionPermissionRequest(
      sync.data.session,
      sync.data.permission,
      props.sessionID(),
      (item) => !permission.autoResponds(item, props.directory),
    ),
  )
  const [permRespondingId, setPermRespondingId] = createSignal<string | undefined>(undefined)
  const permissionResponding = createMemo(() => {
    const p = permissionRequest()
    return Boolean(p && permRespondingId() === p.id)
  })
  function decidePermission(response: "once" | "always" | "reject") {
    const p = permissionRequest()
    if (!p || permRespondingId() === p.id) return
    setPermRespondingId(p.id)
    sdk.client.permission
      .respond({ sessionID: p.sessionID, permissionID: p.id, response })
      .catch((err: unknown) => {
        console.error("[layout:app-sidebar] permission response failed", err)
        showToast({ title: "Permission response could not be sent", description: appSidebarPermissionFailedMessage() })
      })
      .finally(() => setPermRespondingId(undefined))
  }
  const pluginThreads = createMemo(() =>
    appSidebarPluginThreads({
      sessions: sync.data.session,
      directory: props.directory,
      pluginTitle: title(),
      activeSessionID: props.sessionID(),
      messages: sync.data.message,
      parts: sync.data.part as Record<string, Part[] | undefined>,
    }),
  )
  const pluginThreadRows = createMemo(() =>
    pluginThreads()
      .map((thread) => ({
        ...thread,
        time: threadTimeFormatter.format(new Date(thread.updatedAt)),
      }))
      .filter((thread) => {
        const query = state.historyQuery.trim().toLowerCase()
        if (!query) return true
        return `${thread.displayTitle} ${thread.time} ${thread.sessionID}`.toLowerCase().includes(query)
      }),
  )

  const attachmentUnavailable = () =>
    local.model.current()?.capabilities.attachment
      ? "The selected model cannot use app chat files."
      : "The selected model does not support attachments."

  async function addFiles(files: File[]) {
    const model = local.model.current()
    if (!files.length) return
    const inspected = await Promise.all(
      files.map(async (file) => {
        if (file.type.startsWith("video/")) return { file, filename: file.name, mime: file.type }
        return { file, filename: file.name, mime: (await attachmentMime(file)) ?? "" }
      }),
    )
    const boundary = appSidebarVideoBoundary(inspected)
    if (boundary.refusal) showToast(boundary.refusal)
    const candidates = boundary.attachments
    if (!candidates.length) return
    if (!attachmentsEnabled()) {
      showToast({ title: "Attachments unavailable", description: attachmentUnavailable() })
      return
    }

    const valid = (
      await Promise.all(
        candidates.map(async (item) => {
          if (!item.mime || !appChatMimeSupported(model, item.mime)) return []
          const dataUrl = await attachmentData(item.file, item.mime)
          if (!dataUrl) return []
          return [
            {
              id: Identifier.ascending("part"),
              filename: item.file.name,
              mime: item.mime,
              dataUrl,
            },
          ]
        }),
      )
    ).flat()
    if (valid.length) setState("attachments", [...state.attachments, ...valid])
    if (valid.length !== candidates.length) {
      showToast({
        title: valid.length ? "Some attachments were skipped" : "Attachment not supported",
        description: "Choose files supported by the selected model.",
      })
    }
  }

  function pickFiles() {
    if (!attachmentsEnabled()) {
      showToast({ title: "Attachments unavailable", description: attachmentUnavailable() })
      return
    }
    fileInput?.click()
  }

  function removeAttachment(id: string) {
    setState("attachments", (attachments) => attachments.filter((attachment) => attachment.id !== id))
  }

  async function handlePaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData
    if (!clipboardData) return

    const files = appSidebarTransferFiles(clipboardData)
    if (files.length) {
      event.preventDefault()
      event.stopPropagation()
      await addFiles(files)
      return
    }

    const plainText = clipboardData.getData("text/plain") ?? ""
    if (!platform.readClipboardImage || !appSidebarShouldReadNativeClipboard({ fileCount: files.length, plainText })) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    const file = await platform.readClipboardImage()
    if (file) await addFiles([file])
  }

  function handleDragOver(event: DragEvent) {
    if (!event.dataTransfer?.types.includes("Files")) return
    event.preventDefault()
    setState("dragging", true)
  }

  function handleDragLeave(event: DragEvent) {
    const related = event.relatedTarget
    if (related instanceof Node && event.currentTarget instanceof Node && event.currentTarget.contains(related)) return
    setState("dragging", false)
  }

  async function handleDrop(event: DragEvent) {
    const files = appSidebarTransferFiles(event.dataTransfer)
    if (!files.length) return
    event.preventDefault()
    event.stopPropagation()
    setState("dragging", false)
    await addFiles(files)
  }

  function stopGenerationFromEscape(event: KeyboardEvent) {
    // Esc = stop, mirroring the main chat composer. If the run is stopped
    // before it produced any output, the just-sent message comes BACK into
    // the composer (Claude-Code style) instead of sitting in the thread as a
    // dead stopped message.
    if (event.key !== "Escape" || event.defaultPrevented || !working()) return false
    event.preventDefault()
    event.stopPropagation()
    void stopGeneration()
    return true
  }

  function updatePanelFocus() {
    const active = typeof document === "undefined" ? undefined : document.activeElement
    setPanelFocused(active instanceof Node && !!panelRoot?.contains(active))
  }

  function handlePanelKeyDown(event: KeyboardEvent) {
    stopGenerationFromEscape(event)
  }

  function handleComposerKeyDown(event: KeyboardEvent) {
    if (stopGenerationFromEscape(event)) return
    if (!appSidebarShouldSubmitFromKey(event)) return
    event.preventDefault()
    event.currentTarget instanceof HTMLTextAreaElement && event.currentTarget.form?.requestSubmit()
  }

  function resizePromptInput(node: HTMLTextAreaElement | undefined = promptInput) {
    if (!node) return
    node.style.height = "auto"
    node.style.height = `${Math.min(node.scrollHeight, 112)}px`
    node.style.overflowY = node.scrollHeight > 112 ? "auto" : "hidden"
  }

  function queuePromptInputResize() {
    if (promptResizeFrame !== undefined) return
    promptResizeFrame = requestAnimationFrame(() => {
      promptResizeFrame = undefined
      resizePromptInput()
    })
  }

  function focusPromptInput() {
    queueMicrotask(() => promptInput?.focus())
    setState("focusCue", true)
    if (focusCueTimer) clearTimeout(focusCueTimer)
    focusCueTimer = setTimeout(() => setState("focusCue", false), 260)
  }

  function updateScrollbar(node: HTMLElement | undefined = scrollArea) {
    if (!node) return
    setState(
      "scrollbar",
      appSidebarScrollbarState({
        scrollTop: node.scrollTop,
        scrollHeight: node.scrollHeight,
        clientHeight: node.clientHeight,
      }),
    )
  }

  function scrollToBottom() {
    if (scrollArea) scrollArea.scrollTop = scrollArea.scrollHeight
  }
  // Re-pin to the bottom on the next frame, but only if we're still pinned (the
  // user hasn't scrolled away in the meantime). Coalesced to one rAF so a burst of
  // streaming resize events doesn't thrash scrollTop.
  function scheduleStickyScroll() {
    if (!stickToBottom || stickyScrollFrame !== undefined) return
    stickyScrollFrame = requestAnimationFrame(() => {
      stickyScrollFrame = undefined
      if (stickToBottom) scrollToBottom()
    })
  }

  function handleScroll(event: Event) {
    if (event.currentTarget instanceof HTMLElement) {
      const node = event.currentTarget
      updateScrollbar(node)
      // Pin while within a small slack of the bottom; release once the user scrolls
      // up past it. A programmatic scroll-to-bottom lands at distance ~0, so it
      // keeps us pinned (no flag needed to tell the two apart).
      stickToBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= STICK_TO_BOTTOM_THRESHOLD
    }
    setState("scrolling", true)
    if (scrollFadeTimer) clearTimeout(scrollFadeTimer)
    scrollFadeTimer = setTimeout(() => setState("scrolling", false), 900)
  }

  function scrollbarMetrics(track: HTMLElement) {
    if (!scrollArea) return
    const maxScroll = scrollArea.scrollHeight - scrollArea.clientHeight
    if (maxScroll <= 0) return
    const rect = track.getBoundingClientRect()
    const thumbHeight = (state.scrollbar.height / 100) * rect.height
    return {
      maxScroll,
      maxThumbTravel: Math.max(1, rect.height - thumbHeight),
      trackTop: rect.top,
      thumbHeight,
    }
  }

  function setScrollFromThumbTop(top: number, metrics: NonNullable<ReturnType<typeof scrollbarMetrics>>) {
    if (!scrollArea) return
    scrollArea.scrollTop = (Math.max(0, Math.min(metrics.maxThumbTravel, top)) / metrics.maxThumbTravel) * metrics.maxScroll
    updateScrollbar(scrollArea)
  }

  function startScrollbarDrag(event: PointerEvent & { currentTarget: HTMLDivElement }) {
    const metrics = scrollbarMetrics(event.currentTarget)
    if (!metrics || !scrollArea) return
    event.preventDefault()
    event.stopPropagation()
    const target = event.target instanceof Element ? event.target.closest("[data-app-chat-scrollbar-thumb]") : undefined
    if (!target) setScrollFromThumbTop(event.clientY - metrics.trackTop - metrics.thumbHeight / 2, metrics)
    scrollbarDrag = {
      pointerID: event.pointerId,
      startY: event.clientY,
      startScrollTop: scrollArea.scrollTop,
      maxScroll: metrics.maxScroll,
      maxThumbTravel: metrics.maxThumbTravel,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    setState("scrolling", true)
  }

  function moveScrollbarDrag(event: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (!scrollbarDrag || scrollbarDrag.pointerID !== event.pointerId || !scrollArea) return
    event.preventDefault()
    event.stopPropagation()
    scrollArea.scrollTop = Math.max(
      0,
      Math.min(
        scrollbarDrag.maxScroll,
        scrollbarDrag.startScrollTop +
          ((event.clientY - scrollbarDrag.startY) / scrollbarDrag.maxThumbTravel) * scrollbarDrag.maxScroll,
      ),
    )
    updateScrollbar(scrollArea)
  }

  function endScrollbarDrag(event: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (!scrollbarDrag || scrollbarDrag.pointerID !== event.pointerId) return
    event.preventDefault()
    event.stopPropagation()
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    scrollbarDrag = undefined
    if (scrollFadeTimer) clearTimeout(scrollFadeTimer)
    scrollFadeTimer = setTimeout(() => setState("scrolling", false), 900)
  }

  function wheelScrollbarGutter(event: WheelEvent) {
    if (!scrollArea) return
    event.preventDefault()
    scrollArea.scrollTop += event.deltaY
    updateScrollbar(scrollArea)
    setState("scrolling", true)
    if (scrollFadeTimer) clearTimeout(scrollFadeTimer)
    scrollFadeTimer = setTimeout(() => setState("scrolling", false), 900)
  }

  function selectThread(sessionID: string) {
    props.onSessionID(sessionID)
    void sync.session.sync(sessionID, { force: true })
  }

  function startRenameThread(thread: { sessionID: string; displayTitle: string }) {
    setState({
      renamingSessionID: thread.sessionID,
      renameValue: thread.displayTitle,
    })
  }

  function cancelRenameThread() {
    setState({ renamingSessionID: undefined, renameValue: "" })
  }

  function saveRenameThread(thread: { session: Session }) {
    const action = props.onRenameSession
    const nextTitle = state.renameValue.trim()
    if (!action) return
    if (!nextTitle) {
      showToast({
        title: "Name this chat",
        description: "Use a short label that describes the context.",
      })
      return
    }

    void Promise.resolve(
      action(
        thread.session,
        appSidebarPluginFullSessionTitle({
          pluginTitle: title(),
          title: nextTitle,
        }),
      ),
    )
      .then(() => {
        cancelRenameThread()
        void globalSync.project.loadSessions(props.directory)
      })
      .catch((error) => {
        console.error("[layout:app-sidebar] rename failed", error)
        showToast({
          title: "App chat name could not be saved",
          description: appSidebarRenameFailedMessage(),
        })
      })
  }

  function archiveThread(thread: { session: Session; active: boolean }) {
    const action = props.onArchiveSession
    if (!action) return

    void Promise.resolve(action(thread.session))
      .then(() => {
        if (thread.active) props.onNewSession()
        void globalSync.project.loadSessions(props.directory)
      })
      .catch((error) => {
        console.error("[layout:app-sidebar] archive failed", error)
        showToast({
          title: "App chat could not be archived",
          description: appSidebarArchiveFailedMessage(),
        })
      })
  }

  function startNewThread() {
    props.onNewSession()
    setState({ prompt: "", historyQuery: "", renamingSessionID: undefined, renameValue: "", sent: false, attachments: [] })
    focusPromptInput()
  }

  createEffect(on(props.sessionID, (id) => {
    if (!id) return
    void sync.session.sync(id)
    // Switching threads: start parked at the latest message.
    stickToBottom = true
    queueMicrotask(scrollToBottom)
  }))
  createEffect(() => {
    messages().length
    pluginThreads().length
    props.sessionID()
    queueMicrotask(() => {
      updateScrollbar()
      scheduleStickyScroll()
    })
  })
  createEffect(() => {
    state.prompt
    state.attachments.length
    queuePromptInputResize()
  })
  createEffect(() => {
    pluginThreads()
      .slice(0, 10)
      .filter((thread) => sync.data.message[thread.sessionID] === undefined)
      .forEach((thread) => void sync.session.sync(thread.sessionID))
  })
  // "Add to chat" from a canvas artifact (Claude-style): the artifact panel
  // dispatches `oa:add-to-chat` with the source; append it to the composer as a
  // fenced block so the user can ask about / iterate on it here.
  function handleAddToChat(event: Event) {
    const detail = (event as CustomEvent).detail as { text?: string; source?: string } | undefined
    let text = detail?.text?.trim()
    if (!text) return
    const LIMIT = 24000
    const truncated = text.length > LIMIT
    if (truncated) text = `${text.slice(0, LIMIT)}\n… (truncated)`
    const block = "```html\n" + text + "\n```"
    setState("prompt", (p) => (p.trim() ? `${p}\n\n${block}` : block))
    queuePromptInputResize()
    promptInput?.focus()
    showToast({ title: "Added to chat", description: truncated ? "Large source was truncated." : undefined })
  }

  // One-click app→chat: a plugin app action asks its side chat to RUN a prompt.
  // Same renderer-window channel as "Add to chat", but auto-submits immediately.
  const handleRunPrompt = (event: Event) => {
    const detail = (event as CustomEvent<{ text?: string; pluginID?: string; attachments?: unknown[] }>).detail
    const text = typeof detail?.text === "string" ? detail.text.trim() : ""
    if (!text) return
    if (detail?.pluginID && detail.pluginID !== props.pluginID) return // not this app's chat
    if (state.sending) return
    const pluginAttachments = Array.isArray(detail?.attachments)
      ? appSidebarPluginAttachments(detail.attachments)
      : undefined
    const boundary = pluginAttachments ? appSidebarVideoBoundary(pluginAttachments.attachments) : undefined
    const attachments = boundary?.attachments.map((attachment) => ({
      id: Identifier.ascending("part"),
      ...attachment,
    }))
    const refusal = pluginAttachments?.refusal ?? boundary?.refusal
    if (refusal) showToast(refusal)
    setState("prompt", text)
    if (attachments) setState("attachments", attachments)
    queuePromptInputResize()
    void submit()
  }

  onMount(() => {
    void globalSync.project.loadSessions(props.directory)
    queueMicrotask(() => {
      updateScrollbar()
      scrollToBottom()
    })
    queuePromptInputResize()
    // Follow streaming output: the transcript grows token-by-token, which a content
    // change alone doesn't always re-fire effects for, so observe its size directly.
    if (transcriptEl) {
      transcriptResizeObserver = new ResizeObserver(() => scheduleStickyScroll())
      transcriptResizeObserver.observe(transcriptEl)
    }
    window.addEventListener("oa:add-to-chat", handleAddToChat)
    window.addEventListener("oa:run-prompt", handleRunPrompt)
  })
  onCleanup(() => {
    if (scrollFadeTimer) clearTimeout(scrollFadeTimer)
    if (focusCueTimer) clearTimeout(focusCueTimer)
    if (promptResizeFrame !== undefined) cancelAnimationFrame(promptResizeFrame)
    if (stickyScrollFrame !== undefined) cancelAnimationFrame(stickyScrollFrame)
    transcriptResizeObserver?.disconnect()
    window.removeEventListener("oa:add-to-chat", handleAddToChat)
    window.removeEventListener("oa:run-prompt", handleRunPrompt)
  })

  async function submit(event?: SubmitEvent) {
    event?.preventDefault()
    const prompt = state.prompt.trim()
    if ((!prompt && !state.attachments.length) || state.sending) return
    const boundary = appSidebarVideoBoundary(state.attachments)
    if (boundary.refusal) showToast(boundary.refusal)
    if (boundary.attachments.length !== state.attachments.length) setState("attachments", boundary.attachments)
    const attachments = boundary.attachments
    if (!prompt && !attachments.length) return

    const model = local.model.current()
    const agent = local.agent.active()
    if (!model) {
      showToast({
        title: "Select a model",
        description: "Connect a model before chatting with this app.",
      })
      return
    }

    const attachmentError = appChatAttachmentUnsupported(model, attachments)
    if (attachmentError) {
      showToast({
        title: "Attachments unavailable",
        description: attachmentError,
      })
      return
    }

    setState("sending", true)
    // Sending always snaps to the bottom — the user wants to see their message and
    // the reply that follows.
    stickToBottom = true
    scheduleStickyScroll()
    let optimisticSessionID: string | undefined
    const messageID = Identifier.ascending("message")
    const variant = local.model.variant.current()
    const requestParts = appChatRequestParts({ prompt, attachments })
    try {
      const result = await sendAppChatPrompt({
        client: sdk.client,
        directory: props.directory,
        descriptor: descriptor(),
        view: props.page()?.view,
        currentSessionID: props.sessionID(),
        parentSessionID: props.parentSessionID?.() ?? resolvePluginParentSession(Object.values(sync.data.session), props.directory),
        prompt,
        attachments,
        parts: requestParts,
        messageID,
        agent: agent?.name,
        model: { providerID: model.provider.id, modelID: model.id },
        variant,
        onSessionID: (sessionID) => {
          optimisticSessionID = sessionID
          props.onSessionID(sessionID)
          sync.session.optimistic.add({
            sessionID,
            message: {
              id: messageID,
              sessionID,
              role: "user",
              time: { created: Date.now() },
              agent: agent?.name ?? "",
              model: { providerID: model.provider.id, modelID: model.id, variant },
            },
            parts: appChatOptimisticParts({ parts: requestParts, messageID, sessionID }),
          })
        },
      })
      setState({ prompt: "", attachments: [], sent: true })
      void sync.session.sync(result.sessionID, { force: true })
      void globalSync.project.loadSessions(props.directory)
    } catch (error) {
      if (optimisticSessionID) {
        sync.session.optimistic.remove({ sessionID: optimisticSessionID, messageID })
      }
      console.error("[layout:app-sidebar] send failed", error)
      showToast({
        title: "App chat failed",
        description: appSidebarSendFailedMessage(),
      })
    } finally {
      setState("sending", false)
    }
  }

  // The side chat's "a run is in flight" signal — same dual source as the
  // main chat: a non-idle session status OR an assistant message that hasn't
  // completed yet (covers the window before the first status event arrives).
  const working = createMemo(() => {
    if (state.sending) return true
    const sessionID = props.sessionID()
    if (!sessionID) return false
    if ((sync.data.session_status[sessionID] ?? { type: "idle" as const }).type !== "idle") return true
    return (sync.data.message[sessionID] ?? []).some(
      (item) =>
        item.role === "assistant" &&
        item.error?.name !== "MessageAbortedError" &&
        typeof item.time?.completed !== "number",
    )
  })
  const stopping = createMemo(() => working() && !state.prompt.trim() && state.attachments.length === 0)

  // Did the assistant produce ANY visible output after this user message?
  // Text/reasoning with content, or a tool call, counts; bare step markers
  // from an immediately-stopped run do not.
  function producedOutputAfter(sessionID: string, userMessageID: string) {
    return (sync.data.message[sessionID] ?? []).some((item) => {
      if (item.role !== "assistant" || item.id <= userMessageID) return false
      return (sync.data.part[item.id] ?? []).some((part) => {
        if (part.type === "tool") return true
        if (part.type === "text" || part.type === "reasoning") return Boolean(part.text?.trim())
        return false
      })
    })
  }

  async function stopGeneration() {
    const sessionID = props.sessionID()
    if (!sessionID) return
    // Capture the verdict BEFORE aborting — the abort itself finalizes parts.
    const msgs = sync.data.message[sessionID] ?? []
    const lastUser = msgs.findLast((item) => item.role === "user")
    const produced = lastUser ? producedOutputAfter(sessionID, lastUser.id) : true
    sync.set("session_status", sessionID, { type: "idle" })
    await sdk.client.session.abort({ sessionID, directory: props.directory }).catch(() => {})
    if (!lastUser || produced) return
    // Nothing came back: unsend. Restore the text into the composer (only if
    // the user hasn't started typing something new), then remove the message
    // optimistically AND on the server so it doesn't reappear on reload.
    const text = (sync.data.part[lastUser.id] ?? [])
      .flatMap((part) => (part.type === "text" && !part.synthetic && part.text ? [part.text] : []))
      .join("\n")
      .trim()
    if (text && !state.prompt.trim()) {
      setState("prompt", text)
      queuePromptInputResize()
      focusPromptInput()
    }
    for (const item of msgs) {
      // The stopped run's EMPTY assistant shells go too — they render as
      // dead bubbles otherwise.
      if (item.role !== "assistant" || item.id <= lastUser.id) continue
      sync.session.optimistic.remove({ sessionID, messageID: item.id })
      void sdk.client.session
        .deleteMessage({ sessionID, messageID: item.id, directory: props.directory })
        .catch(() => {})
    }
    sync.session.optimistic.remove({ sessionID, messageID: lastUser.id })
    void sdk.client.session
      .deleteMessage({ sessionID, messageID: lastUser.id, directory: props.directory })
      .catch(() => {})
  }

  command.register("app-sidebar-panel", () => [
    {
      id: "appChat.stop",
      title: "Stop response",
      category: "Session",
      keybind: "escape",
      disabled: !working() || !panelFocused(),
      onSelect: () => {
        void stopGeneration()
      },
    },
  ])

  return (
    <div
      ref={(node) => {
        panelRoot = node
      }}
      data-app-sidebar-panel
      data-plugin-id={props.pluginID}
      class="group/app-chat-scroll relative flex min-h-0 flex-1 flex-col bg-background-base py-3"
      onKeyDown={handlePanelKeyDown}
      onFocusIn={updatePanelFocus}
      onFocusOut={() => queueMicrotask(updatePanelFocus)}
    >
      <div class="shrink-0 border-b border-border-weaker-base px-4 pb-3">
        <div class="flex min-w-0 items-center gap-3">
          <div
            data-app-chat-plugin-icon
            class="flex size-10 shrink-0 items-center justify-center rounded-xl border border-border-weaker-base bg-background-secondary/80 text-text-strong"
            style={accentStyle()}
          >
            <PluginAppIcon
              id={descriptor().id}
              icon={railSyncedIcon()}
              title={title()}
              capabilities={descriptor().capabilities}
              size="normal"
              brandPath={railSyncedBrandPath()}
            />
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-15-medium text-text-strong">{title()} App Chat</div>
            <div class="mt-0.5 truncate text-12-regular text-text-weak">{displayFilename(props.directory)}</div>
          </div>
          <Show when={sessionHref()}>
            {(href) => (
              <A
                href={href()}
                class="shrink-0 rounded-lg border border-border-weaker-base bg-background-secondary/55 px-2.5 py-1.5 text-12-medium text-text-base transition-colors hover:border-border-weak-base hover:bg-background-secondary hover:text-text-strong"
              >
                Open backing session
              </A>
            )}
          </Show>
        </div>
      </div>

      <div class="relative min-h-0 flex-1">
        <div
          ref={(node) => {
            scrollArea = node
          }}
          data-app-chat-scroll
          class="no-scrollbar h-full min-h-0 overflow-y-auto pb-3 pl-4 pr-7"
          onScroll={handleScroll}
        >
          <div data-app-chat-history class="border-b border-border-weaker-base py-2">
            <div data-app-chat-history-heading class="flex h-9 w-full items-center justify-between gap-3 rounded-md px-2.5">
              <div class="flex min-w-0 items-center gap-3">
                <Icon name="speech-bubble" size="small" class="shrink-0 text-icon-base" />
                <span class="truncate text-14-medium font-bold text-text-strong">Chats</span>
              </div>
              <button
                type="button"
                data-action="app-chat-new-chat"
                class="group/navrow inline-flex h-8 shrink-0 items-center gap-2 rounded-md px-2 text-13-medium text-text-base transition-colors hover:bg-surface-raised-base-hover hover:text-text-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
                onClick={startNewThread}
              >
                <Icon name="new-session" size="small" class="shrink-0 text-icon-base group-hover/navrow:text-icon-strong" />
                <span>New chat</span>
              </button>
            </div>
            <Show when={pluginThreads().length > 6}>
              <input
                data-app-chat-history-search
                value={state.historyQuery}
                onInput={(event) => setState("historyQuery", event.currentTarget.value)}
                placeholder="Search chats"
                class="mt-2 h-8 w-full rounded-lg border border-border-weaker-base bg-background-base px-2.5 text-12-regular text-text-strong placeholder:text-text-weak focus:outline-none focus:ring-1 focus:ring-border-active"
              />
            </Show>
            <Show
              when={pluginThreads().length > 0}
              fallback={
                <div class="mt-2 px-2 py-2 text-12-regular text-text-weak">No app chats yet.</div>
              }
            >
              <div
                data-app-chat-history-list
                class="no-scrollbar mt-2 flex max-h-44 flex-col gap-0.5 overflow-y-auto overscroll-contain pr-0.5"
              >
                <Show
                  when={pluginThreadRows().length > 0}
                  fallback={<div class="px-2 py-3 text-12-regular text-text-weak">No matching chats.</div>}
                >
                  <For each={pluginThreadRows()}>
                    {(thread) => (
                      <div
                        data-app-chat-thread
                        data-active={thread.active ? "true" : "false"}
                        classList={{
                          "group/app-chat-thread flex min-h-8 items-center gap-1 rounded-md px-1 py-0.5 transition-colors duration-150":
                            true,
                          "bg-surface-interactive-weak/55 text-text-strong": thread.active,
                          "text-text-base hover:bg-surface-interactive-weak/60 hover:text-text-strong": !thread.active,
                        }}
                      >
                        <Show
                          when={state.renamingSessionID === thread.sessionID}
                          fallback={
                            <>
                              <button
                                type="button"
                                class="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-left focus:outline-none focus:ring-1 focus:ring-border-active"
                                onClick={() => selectThread(thread.sessionID)}
                              >
                                <span
                                  classList={{
                                    "size-1.5 shrink-0 rounded-full": true,
                                    "bg-border-active": thread.active,
                                    "bg-border-weak-base": !thread.active,
                                  }}
                                />
                                <span class="flex min-w-0 flex-1 items-center gap-2">
                                  <span data-app-chat-thread-title class="min-w-0 flex-1 truncate text-12-medium">
                                    {thread.displayTitle}
                                  </span>
                                  <span class="shrink-0 text-11-regular text-text-weak">{thread.time}</span>
                                </span>
                              </button>
                              <div
                                data-app-chat-thread-action
                                class="flex shrink-0 items-center gap-0.5 opacity-60 transition-opacity group-hover/app-chat-thread:opacity-100 group-focus-within/app-chat-thread:opacity-100"
                              >
                                <Show when={props.onRenameSession}>
                                  <IconButton
                                    type="button"
                                    data-app-chat-thread-rename
                                    icon="edit"
                                    size="small"
                                    variant="ghost"
                                    aria-label="Rename app chat"
                                    title="Rename"
                                    onClick={() => startRenameThread(thread)}
                                  />
                                </Show>
                                <Show when={props.onArchiveSession}>
                                  <IconButton
                                    type="button"
                                    data-app-chat-thread-archive
                                    icon="archive"
                                    size="small"
                                    variant="ghost"
                                    aria-label="Archive app chat"
                                    title="Archive"
                                    onClick={() => archiveThread(thread)}
                                  />
                                </Show>
                              </div>
                            </>
                          }
                        >
                          <form
                            class="flex min-w-0 flex-1 items-center gap-1.5 px-1"
                            onSubmit={(event) => {
                              event.preventDefault()
                              saveRenameThread(thread)
                            }}
                          >
                            <input
                              data-app-chat-thread-rename-input
                              value={state.renameValue}
                              autofocus
                              onInput={(event) => setState("renameValue", event.currentTarget.value)}
                              onKeyDown={(event) => {
                                if (event.key !== "Escape") return
                                event.preventDefault()
                                cancelRenameThread()
                              }}
                              class="h-8 min-w-0 flex-1 rounded-md border border-border-weaker-base bg-background-base px-2 text-12-regular text-text-strong focus:outline-none focus:ring-1 focus:ring-border-active"
                            />
                            <IconButton
                              type="submit"
                              data-app-chat-thread-action
                              icon="check"
                              size="small"
                              variant="primary"
                              aria-label="Save chat name"
                              title="Save"
                            />
                            <IconButton
                              type="button"
                              data-app-chat-thread-action
                              icon="close-small"
                              size="small"
                              variant="ghost"
                              aria-label="Cancel rename"
                              title="Cancel"
                              onClick={cancelRenameThread}
                            />
                          </form>
                        </Show>
                      </div>
                    )}
                  </For>
                </Show>
              </div>
            </Show>
          </div>

          <div
            data-app-chat-transcript
            ref={(node) => {
              transcriptEl = node
            }}
            class="mt-4 flex flex-col gap-3"
          >
            <Show
              when={messages().length > 0}
              fallback={
                <div class="px-3 py-5 text-center text-12-regular text-text-weak">No messages yet.</div>
              }
            >
              {/* Same renderer as the main chat (SessionTurn) — user/assistant
                  bubbles, live streaming, and tool-call chips — bound to THIS
                  plugin's app-chat session. The side panel renders OUTSIDE the
                  route's DataProvider, so we provide the same Data context here,
                  fed by this panel's own sync store. */}
              <DataProvider
                data={sync.data}
                directory={props.directory}
                onSessionHref={(id: string) => `/${props.slug}/session/${id}`}
              >
                <For each={messages()}>
                  {(message) => (
                    <SessionTurn
                      sessionID={props.sessionID() ?? ""}
                      messageID={message.id}
                      classes={{
                        root: "min-w-0 w-full relative",
                        content: "flex flex-col justify-between !overflow-visible",
                        container: "w-full",
                      }}
                    />
                  )}
                </For>
              </DataProvider>
            </Show>
          </div>
        </div>
      </div>
      <Show when={state.scrollbar.visible}>
        <div
          data-app-chat-scroll-gutter
          classList={{
            "pointer-events-auto absolute bottom-0 right-2 top-0 w-3 cursor-pointer touch-none opacity-0 transition-opacity duration-200 group-hover/app-chat-scroll:opacity-100":
              true,
            "opacity-100": state.scrolling,
          }}
          onPointerDown={startScrollbarDrag}
          onPointerMove={moveScrollbarDrag}
          onPointerUp={endScrollbarDrag}
          onPointerCancel={endScrollbarDrag}
          onWheel={wheelScrollbarGutter}
        >
          <div
            data-app-chat-scrollbar-thumb
            class="absolute right-0 w-1.5 rounded-full"
            style={{
              height: `${state.scrollbar.height}%`,
              top: `${state.scrollbar.top}%`,
            }}
          />
        </div>
      </Show>

      <Show when={permissionRequest()} keyed>
        {(request) => (
          <div class="no-scrollbar max-h-[50vh] shrink-0 overflow-y-auto px-2 pt-3" data-app-chat-permission-dock>
            <SessionPermissionDock request={request} responding={permissionResponding()} onDecide={decidePermission} />
          </div>
        )}
      </Show>

      <Show when={questionRequest()} keyed>
        {(request) => (
          <div class="no-scrollbar max-h-[50vh] shrink-0 overflow-y-auto px-2 pt-3" data-app-chat-question-dock>
            <SessionQuestionDock
              request={request}
              onSubmit={() => {
                const id = props.sessionID()
                if (!id) return
                void sync.session.sync(id, { force: true })
              }}
            />
          </div>
        )}
      </Show>

      <form data-app-chat-composer class="shrink-0 px-4 pt-3" onSubmit={submit}>
        <input
          ref={(el) => {
            fileInput = el
          }}
          type="file"
          multiple
          class="hidden"
          accept={acceptedFileTypes().join(",")}
          onChange={(event) => {
            void addFiles(Array.from(event.currentTarget.files ?? []))
            event.currentTarget.value = ""
          }}
        />
        <div
          data-app-chat-composer-shell
          data-dock-surface="shell"
          data-dragging={state.dragging ? "true" : "false"}
          class="@container group/prompt-input relative min-h-[98px] overflow-hidden rounded-[24px] transition-[box-shadow,border-color] duration-150 focus-within:shadow-xs-border"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(event) => void handleDrop(event)}
        >
          <Show when={state.dragging}>
            <div class="pointer-events-none absolute inset-0 z-10 grid place-items-center bg-background-base/85 text-13-medium text-text-strong backdrop-blur-sm">
              Drop to attach
            </div>
          </Show>
          <Show when={state.focusCue}>
            <div aria-hidden="true" data-app-chat-focus-cue />
          </Show>
          <textarea
            ref={(node) => {
              promptInput = node
              queuePromptInputResize()
            }}
            value={state.prompt}
            onInput={(event) => {
              setState("prompt", event.currentTarget.value)
              resizePromptInput(event.currentTarget)
            }}
            onPaste={(event) => {
              void handlePaste(event)
              queuePromptInputResize()
            }}
            onKeyDown={handleComposerKeyDown}
            placeholder={`Chat with ${title()}...`}
            rows={1}
            class="no-scrollbar block max-h-28 min-h-10 w-full resize-none appearance-none border-0 bg-transparent px-4 pb-1.5 pt-3 text-14-regular leading-5 text-text-strong shadow-none placeholder:text-text-weak focus:outline-none focus:ring-0"
          />
          <Show when={state.attachments.length > 0}>
            <div data-app-chat-attachment-list class="flex flex-wrap gap-1.5 px-4 py-2">
              <For each={state.attachments}>
                {(attachment) => (
                  <span class="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border-weaker-base bg-background-secondary/70 px-2 py-1 text-11-regular text-text-base">
                    <Icon name={attachment.mime.startsWith("image/") ? "photo" : "folder"} size="small" />
                    <span class="max-w-36 truncate">{attachment.filename}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${attachment.filename}`}
                      class="text-text-weak transition-colors hover:text-text-strong"
                      onClick={() => removeAttachment(attachment.id)}
                    >
                      <Icon name="close-small" class="size-3" />
                    </button>
                  </span>
                )}
              </For>
            </div>
          </Show>
          <div data-app-chat-composer-strip class="flex items-center justify-end gap-1 px-2 py-2 @[23rem]:gap-2 @[23rem]:px-3">
            <DropdownMenu
              placement="top-start"
              gutter={8}
              open={permissionMenuOpen()}
              onOpenChange={setPermissionMenuOpen}
            >
              <DropdownMenu.Trigger
                as="button"
                type="button"
                data-action="prompt-permission"
                data-app-chat-permission
                class="inline-flex h-8 w-8 max-w-8 shrink-0 items-center justify-center gap-0 rounded-full border px-0 text-13-medium transition-[background-color,border-color,color] data-[expanded]:bg-surface-raised-base-active @[18rem]:w-auto @[18rem]:max-w-[148px] @[18rem]:justify-start @[18rem]:gap-1.5 @[18rem]:px-2"
                classList={{
                  "border-border-weaker-base bg-surface-raised-base/80 text-icon-critical-base hover:border-border-weak-base hover:bg-surface-critical-weak":
                    currentPermissionMode() === "full-access",
                  "border-border-weaker-base bg-surface-raised-base/80 text-icon-warning-active hover:border-border-weak-base hover:bg-surface-raised-base-hover":
                    currentPermissionMode() === "auto-review",
                  "border-border-weaker-base bg-surface-raised-base/80 text-text-base hover:border-border-weak-base hover:bg-surface-raised-base-hover":
                    currentPermissionMode() === "default",
                }}
                aria-label="Permission mode"
                title={currentPermissionOption().label}
              >
                <Icon
                  name={currentPermissionOption().icon}
                  size="small"
                  data-app-chat-permission-icon
                  class="shrink-0"
                />
                <span data-app-chat-permission-label class="hidden @[18rem]:block @[18rem]:truncate">
                  {currentPermissionOption().label}
                </span>
                <Icon
                  name="chevron-down"
                  size="small"
                  data-app-chat-permission-chevron
                  class="hidden @[18rem]:block shrink-0"
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content class="mb-1 min-w-36">
                  <DropdownMenu.Group>
                    <DropdownMenu.RadioGroup
                      value={currentPermissionMode()}
                      onChange={(value) => {
                        if (typeof value === "string" && isPermissionMode(value)) selectPermissionMode(value)
                      }}
                    >
                      <For each={PERMISSION_MODE_OPTIONS}>
                        {(option) => (
                          <DropdownMenu.RadioItem
                            value={option.value}
                            onSelect={() => {
                              if (option.value !== currentPermissionMode()) return
                              setPermissionMenuOpen(false)
                            }}
                          >
                            <Icon name={option.icon} size="small" class={option.iconClass} />
                            <DropdownMenu.ItemLabel>{option.label}</DropdownMenu.ItemLabel>
                            <DropdownMenu.ItemIndicator>
                              <Icon name="check-small" size="small" />
                            </DropdownMenu.ItemIndicator>
                          </DropdownMenu.RadioItem>
                        )}
                      </For>
                    </DropdownMenu.RadioGroup>
                  </DropdownMenu.Group>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu>
            <div class="min-w-0 flex-1" />
            <Button
              type="button"
              variant="ghost"
              icon="plus"
              data-action="prompt-attach"
              data-app-chat-composer-action
              class="size-8 shrink-0 p-0"
              disabled={!attachmentsEnabled() || state.sending}
              aria-label={attachmentsEnabled() ? "Attach files" : attachmentUnavailable()}
              title={attachmentsEnabled() ? "Attach files" : attachmentUnavailable()}
              onClick={pickFiles}
            />
            <IconButton
              type={stopping() ? "button" : "submit"}
              size="large"
              data-action="prompt-submit"
              data-app-chat-composer-submit
              icon={stopping() ? "stop" : "arrow-up"}
              variant="primary"
              class="size-8 rounded-full"
              disabled={!stopping() && !canSubmit()}
              aria-label={stopping() ? "Stop response" : state.sending ? "Sending message" : "Send message"}
              title={stopping() ? "Stop response" : state.sending ? "Sending message" : "Send message"}
              onClick={(event) => {
                if (!stopping()) return
                event.preventDefault()
                event.stopPropagation()
                void stopGeneration()
              }}
            />
          </div>
        </div>
      </form>
    </div>
  )
}
